import { ApolloError } from "@apollo/client";
import { useLoadScript } from "@react-google-maps/api";
import type { Position } from "geojson";
import Immutable from "immutable";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  expandBoundaryMapResponse,
  fetchOptimizedBoundaryMap,
} from "src/components/Boundary/fetchOptimizedBoundaryMap";
import {
  GET_BOUNDARIES_BY_ENROLLMENT_PERIOD,
  GET_BOUNDARIES_BY_FORM,
} from "src/components/Boundary/queries";
import {
  BoundaryMapResponse,
  SchoolBoundaries,
} from "src/components/Boundary/schemas";
import * as Boundary from "src/components/Boundary/services";
import { useFormAddresForFilter } from "src/components/Form/hooks/useFormAddressForFilter";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  getBoundaryMapByEnrollmentPeriodIdOptimized,
  parentGetBoundaryMapByEnrollmentPeriodIdOptimized,
} from "src/generated/hooks/form-service/requests";
import useCheckUserRole from "src/hooks/useCheckUserRole";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";

const googleApiLibraries: Array<"places"> = ["places"];

export function useFormAddressPosition({ formId }: { formId: uuid }) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY || "",
    libraries: googleApiLibraries,
  });
  const [position, setPosition] = useState<
    RD.RemoteData<Error, Position | undefined>
  >(RD.notAsked());

  const { remoteData, refetch } = useFormAddresForFilter({ formId });

  useEffect(() => {
    if (remoteData.hasData() && isLoaded) {
      setPosition(RD.loading());
      if (remoteData.data?.geom) {
        const pos = remoteData.data?.geom.coordinates;
        setPosition(RD.success([pos[0], pos[1]]));
      } else {
        Boundary.addressLookup(new google.maps.Geocoder(), remoteData.data)
          .then((location) => {
            setPosition(RD.success(location));
          })
          .catch((e) => setPosition(RD.failure(e)));
      }
    }
  }, [isLoaded, remoteData]);

  return { remoteData: position, refetch };
}

export function useBoundariesMap({ formId }: { formId: uuid }) {
  const flags = useFlags(["boundary-management"]);
  const boundaryManagementEnabled =
    flags["boundary-management"]?.enabled ?? false;
  const { isGuardian } = useCheckUserRole();

  const { remoteData } = useRemoteDataQuery<
    GQL.GetBoundariesByForm,
    GQL.GetBoundariesByFormVariables
  >(GET_BOUNDARIES_BY_FORM, {
    variables: {
      form_id: formId,
    },
    skip: boundaryManagementEnabled,
  });

  const [formServiceData, setFormServiceData] = useState<
    RD.RemoteData<Error, Immutable.Map<uuid, SchoolBoundaries>>
  >(RD.notAsked());

  useEffect(() => {
    if (boundaryManagementEnabled) {
      setFormServiceData(RD.loading());
      fetchOptimizedBoundaryMap(formId, isGuardian)
        .then((boundariesMap) => {
          setFormServiceData(RD.success(boundariesMap));
        })
        .catch((error) => {
          setFormServiceData(
            RD.failure(
              error instanceof Error ? error : new Error(JSON.stringify(error))
            )
          );
        });
    }
  }, [boundaryManagementEnabled, isGuardian, formId]);

  const boundariesMapRemoteData = useMemo(() => {
    if (boundaryManagementEnabled) {
      return formServiceData;
    }
    return remoteData.map((data) => {
      const boundariesMap = Boundary.fromGQL(data);
      return boundariesMap;
    });
  }, [remoteData, boundaryManagementEnabled, formServiceData]);

  return boundariesMapRemoteData.mapError((e) =>
    e instanceof ApolloError ? e : new ApolloError({ errorMessage: e.message })
  );
}

export function useBoundariesMapByEnrollmentPeriod({
  enrollmentPeriodId,
}: {
  enrollmentPeriodId: uuid;
}) {
  const flags = useFlags(["boundary-management"]);
  const boundaryManagementEnabled =
    flags["boundary-management"]?.enabled ?? false;
  const { isGuardian } = useCheckUserRole();

  const { remoteData, refetch: refetchGraphQL } = useRemoteDataQuery<
    GQL.GetBoundariesByEnrollmentPeriod,
    GQL.GetBoundariesByEnrollmentPeriodVariables
  >(GET_BOUNDARIES_BY_ENROLLMENT_PERIOD, {
    variables: {
      enrollmentPeriodId,
    },
    skip: boundaryManagementEnabled,
  });

  const [formServiceData, setFormServiceData] = useState<
    RD.RemoteData<Error, Immutable.Map<uuid, SchoolBoundaries>>
  >(RD.notAsked());

  const fetchFormServiceData = useCallback(() => {
    if (boundaryManagementEnabled) {
      setFormServiceData(RD.loading());
      const fetchFunction = isGuardian
        ? parentGetBoundaryMapByEnrollmentPeriodIdOptimized
        : getBoundaryMapByEnrollmentPeriodIdOptimized;

      fetchFunction({
        path: {
          enrollmentPeriodId,
        },
      })
        .then((response) => {
          const apiResponse = response.data
            ?.data as unknown as BoundaryMapResponse;
          const boundariesMap = expandBoundaryMapResponse(apiResponse);
          setFormServiceData(RD.success(boundariesMap));
        })
        .catch((error) => {
          setFormServiceData(
            RD.failure(
              error instanceof Error ? error : new Error(JSON.stringify(error))
            )
          );
        });
    }
  }, [boundaryManagementEnabled, isGuardian, enrollmentPeriodId]);

  useEffect(() => {
    fetchFormServiceData();
  }, [fetchFormServiceData]);

  const boundariesMapRemoteData = useMemo(() => {
    if (boundaryManagementEnabled) {
      return formServiceData;
    }
    return remoteData.map((data) => {
      const boundariesMap = Boundary.fromGQL(data);
      return boundariesMap;
    });
  }, [remoteData, boundaryManagementEnabled, formServiceData]);

  const refetch = useCallback(async () => {
    if (boundaryManagementEnabled) {
      fetchFormServiceData();
    } else {
      await refetchGraphQL();
    }
  }, [boundaryManagementEnabled, fetchFormServiceData, refetchGraphQL]);

  return {
    remoteData: boundariesMapRemoteData.mapError((e) =>
      e instanceof ApolloError
        ? e
        : new ApolloError({ errorMessage: e.message })
    ),
    refetch,
  };
}
